import * as React from "react"
import { Control, Emitter } from "rete"
import { EventsTypes } from "rete/types/events"

interface TextNumericFieldProps {
  emitter: Emitter<EventsTypes> | null
  getTextValue: () => string
  setTextValue: (value: string) => void
  getNumericValue: () => string
  setNumericValue: (value: string) => void
  textTitle: string
  numericTitle: string
  textWidth: string
  numericWidth: string
  /** Renders beside a Rete input socket: one light strip with inline fields */
  variant: "default" | "socket-row"
  getIsDisabled: () => boolean
}

type EmitterWithOff = Emitter<EventsTypes> & {
  off: (event: string, handler: (...args: unknown[]) => void) => void
}

const hasEmitterOff = (emitter: Emitter<EventsTypes> | null): emitter is EmitterWithOff =>
  !!emitter && typeof (emitter as Partial<EmitterWithOff>).off === "function"

const TextNumericField = ({
  emitter,
  getTextValue,
  setTextValue,
  getNumericValue,
  setNumericValue,
  textTitle,
  numericTitle,
  textWidth,
  numericWidth,
  variant,
  getIsDisabled,
}: TextNumericFieldProps) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)

  React.useEffect(() => {
    if (!emitter) return

    const bumpDisabled = () => queueMicrotask(() => forceUpdate())

    emitter.on("connectioncreated", bumpDisabled)
    emitter.on("connectionremoved", bumpDisabled)
    emitter.on("process", bumpDisabled)

    return () => {
      if (hasEmitterOff(emitter)) {
        emitter.off("connectioncreated", bumpDisabled)
        emitter.off("connectionremoved", bumpDisabled)
        emitter.off("process", bumpDisabled)
      }
    }
  }, [emitter])

  const disabled = variant === "socket-row" ? getIsDisabled() : false

  if (variant === "socket-row") {
    const caption = (textTitle && textTitle.trim()) || "Label"
    return (
      <div className="text-numeric-control--socket-stack">
        <div className="input-title text-numeric-control__caption">{caption}</div>
        <div className={`text-numeric-control--socket-row${disabled ? " is-disabled" : ""}`}>
          <input
            type="number"
            className="form-control text-numeric-control__input text-numeric-control__input--numeric-only"
            aria-label={numericTitle || caption}
            placeholder={numericTitle || "Numeric input"}
            value={getNumericValue()}
            disabled={disabled}
            onChange={e => {
              setNumericValue(e.target.value)
              forceUpdate()
            }}
            onPointerDown={e => e.stopPropagation()}
            onDoubleClick={e => e.stopPropagation()}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.35rem",
        alignItems: "flex-end",
      }}
    >
      <div>
        <label>{textTitle}</label>
        <input
          type="text"
          className="form-control"
          value={getTextValue()}
          onChange={e => {
            setTextValue(e.target.value)
            forceUpdate()
          }}
          onPointerDown={e => e.stopPropagation()}
          onDoubleClick={e => e.stopPropagation()}
          style={{ width: textWidth }}
        />
      </div>
      <div>
        <label>{numericTitle}</label>
        <input
          type="number"
          className="form-control"
          value={getNumericValue()}
          onChange={e => {
            setNumericValue(e.target.value)
            forceUpdate()
          }}
          onPointerDown={e => e.stopPropagation()}
          onDoubleClick={e => e.stopPropagation()}
          style={{ width: numericWidth }}
        />
      </div>
    </div>
  )
}

export class TextNumericControl extends Control {
  props: TextNumericFieldProps
  component: (props: TextNumericFieldProps) => JSX.Element
  type: string
  /** Last-known values when `getNode()` fails during teardown (e.g. `connectionremoved` → microtask render). */
  private dataShadow: Record<string, unknown> = {}

  constructor(
    emitter: Emitter<EventsTypes> | null,
    textKey: string,
    numericKey: string,
    textTitle: string = "",
    numericTitle: string = "",
    textWidth: string = "120px",
    numericWidth: string = "100px",
    socketRow: boolean = false,
    getConnected?: () => boolean
  ) {
    super(`${textKey}:${numericKey}`)
    this.type = "TextNumericControl"

    const process = () => emitter?.trigger("process")
    const readFromNode = (key: string): unknown => {
      try {
        const v = this.getData(key)
        this.dataShadow[key] = v
        return v
      } catch {
        return this.dataShadow[key]
      }
    }
    const writeToNode = (key: string, data: unknown) => {
      try {
        this.putData(key, data)
        this.dataShadow[key] = data
      } catch {
        this.dataShadow[key] = data
      }
    }
    const readText = (): string => {
      const value = readFromNode(textKey)
      return typeof value === "string" ? value : ""
    }
    const readNumericString = (): string => {
      const value = readFromNode(numericKey)
      if (typeof value === "number" && !Number.isNaN(value)) return String(value)
      if (typeof value === "string") return value
      return ""
    }

    this.props = {
      emitter,
      getTextValue: readText,
      setTextValue: value => {
        writeToNode(textKey, value)
        process()
      },
      getNumericValue: readNumericString,
      setNumericValue: value => {
        const parsed = Number(value)
        if (value === "" || Number.isNaN(parsed)) {
          writeToNode(numericKey, value)
        } else {
          writeToNode(numericKey, parsed)
        }
        process()
      },
      textTitle,
      numericTitle,
      textWidth,
      numericWidth,
      variant: socketRow ? "socket-row" : "default",
      getIsDisabled: () => {
        if (getConnected) {
          try {
            return getConnected()
          } catch {
            return false
          }
        }
        try {
          const parent = this.parent as { hasConnection?: () => boolean; connections?: unknown[] } | null
          if (parent && typeof parent.hasConnection === "function") {
            return parent.hasConnection()
          }
          if (parent?.connections) {
            return parent.connections.length > 0
          }
          return false
        } catch {
          return false
        }
      },
    }
    this.component = TextNumericField
  }
}
